import React from "react";
import FilterPill from "../common/filter_pill";
import Skeleton from "../common/skeleton";
import EmptyState from "../common/empty_state";
import { formatKst } from "../../utils/time";

/**
 * WalletStatusTable -- 코인별 입출금 상태 테이블 컴포넌트
 *
 * Props:
 * - data (Array): walletStatus 배열 [{id, exchange, currency, net_type, deposit_status, withdrawal_status, updated_at}]
 * - loading (boolean): 로딩 중 여부
 * - lastUpdated (Date|null): 마지막 업데이트 시각
 * - searchQuery (string): 코인 검색어
 * - statusFilter (string|null): 'normal' | 'suspended' | null(전체)
 * - onSearchChange (function): 검색어 변경 핸들러
 * - onStatusFilterChange (function): 상태 필터 변경 핸들러
 */
function WalletStatusTable({
    data = [],
    loading = false,
    lastUpdated = null,
    searchQuery = "",
    statusFilter = null,
    onSearchChange,
    onStatusFilterChange,
    highlightIds = [],
    error = false,
    listedOnly = true,
    onListedOnlyChange,
    marketFilter = null,
    onMarketFilterChange,
}) {
    /* ── 필터링 로직 ── */
    const filtered = data.filter((item) => {
        // 검색어 필터: currency를 소문자로 비교
        if (searchQuery) {
            const query = searchQuery.toLowerCase();
            if (!item.currency?.toLowerCase().includes(query)) {
                return false;
            }
        }
        // 상태 필터
        if (statusFilter === "normal") {
            if (item.deposit_status !== "normal" || item.withdrawal_status !== "normal") return false;
        }
        if (statusFilter === "suspended") {
            if (item.deposit_status === "normal" && item.withdrawal_status === "normal") return false;
        }
        // 마켓 필터
        if (marketFilter && item.markets) {
            if (!item.markets.includes(marketFilter)) return false;
        }
        return true;
    });

    /* ── 마켓 배지 렌더링 ── */
    const marketColors = { KRW: "text-brand-light bg-brand-subtle", BTC: "text-warning bg-warning-subtle", USDT: "text-gain bg-gain-subtle" };
    const renderMarketBadges = (markets) => {
        if (!markets || markets.length === 0) return <span className="text-xs text-content-muted">-</span>;
        return (
            <div className="flex gap-1">
                {markets.map((m) => (
                    <span key={m} className={`inline-flex rounded px-1.5 py-0.5 text-xs font-semibold ${marketColors[m] || "text-content-secondary bg-surface-elevated"}`}>
                        {m}
                    </span>
                ))}
            </div>
        );
    };

    /* ── 상태 배지 렌더링 ── */
    const renderStatusBadge = (status) => {
        if (status === "normal") {
            return (
                <span className="inline-flex items-center rounded-full bg-gain-subtle px-2 py-0.5 text-xs font-medium text-gain">
                    정상
                </span>
            );
        }
        return (
            <span className="inline-flex items-center rounded-full bg-loss-subtle px-2 py-0.5 text-xs font-medium text-loss">
                정지
            </span>
        );
    };

    /* ── 로딩 상태 ── */
    if (loading) {
        return (
            <div className="rounded-2xl border border-surface-border bg-surface-card p-4 shadow-card sm:p-6">
                <div className="flex flex-col gap-3">
                    {Array.from({ length: 5 }).map((_, i) => (
                        <Skeleton key={i} variant="rect" height="40px" />
                    ))}
                </div>
            </div>
        );
    }

    /* ── 에러 상태 ── */
    if (error) {
        return (
            <div className="rounded-2xl border border-surface-border bg-surface-card p-4 shadow-card sm:p-6">
                <div className="rounded-lg border border-loss/20 bg-loss/5 p-4 text-center text-sm text-loss">
                    데이터를 불러올 수 없습니다.
                </div>
            </div>
        );
    }

    return (
        <div className="rounded-2xl border border-surface-border bg-surface-card p-4 shadow-card sm:p-6">
            {/* 검색 + 필터 바 */}
            <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <input
                    type="text"
                    aria-label="코인 검색"
                    placeholder="코인 검색..."
                    value={searchQuery}
                    onChange={onSearchChange}
                    className="w-full sm:w-48 rounded-lg border border-surface-border bg-surface-elevated px-3 py-2 text-sm text-content placeholder:text-content-muted focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand"
                />
                <div className="flex flex-wrap items-center gap-2">
                    <FilterPill
                        active={statusFilter === null}
                        onClick={() => onStatusFilterChange(null)}
                        variant="subtle"
                        colorScheme="brand"
                    >
                        전체
                    </FilterPill>
                    <FilterPill
                        active={statusFilter === "normal"}
                        onClick={() => onStatusFilterChange("normal")}
                        variant="subtle"
                        colorScheme="gain"
                    >
                        정상
                    </FilterPill>
                    <FilterPill
                        active={statusFilter === "suspended"}
                        onClick={() => onStatusFilterChange("suspended")}
                        variant="subtle"
                        colorScheme="loss"
                    >
                        정지
                    </FilterPill>
                    {onMarketFilterChange && (
                        <>
                            <span className="ml-2 border-l border-surface-border pl-2" />
                            <FilterPill active={marketFilter === null} onClick={() => onMarketFilterChange(null)} variant="subtle" colorScheme="brand">전체 마켓</FilterPill>
                            <FilterPill active={marketFilter === "KRW"} onClick={() => onMarketFilterChange("KRW")} variant="subtle" colorScheme="brand">KRW</FilterPill>
                            <FilterPill active={marketFilter === "BTC"} onClick={() => onMarketFilterChange("BTC")} variant="subtle" colorScheme="brand">BTC</FilterPill>
                            <FilterPill active={marketFilter === "USDT"} onClick={() => onMarketFilterChange("USDT")} variant="subtle" colorScheme="brand">USDT</FilterPill>
                        </>
                    )}
                    {onListedOnlyChange && (
                        <span className="ml-2 border-l border-surface-border pl-2">
                            <label className="flex items-center gap-1.5 cursor-pointer text-xs text-content-secondary">
                                <input
                                    type="checkbox"
                                    checked={listedOnly}
                                    onChange={(e) => onListedOnlyChange(e.target.checked)}
                                    className="h-3.5 w-3.5 rounded border-surface-border text-brand focus:ring-brand accent-brand"
                                />
                                상장 코인만
                            </label>
                        </span>
                    )}
                </div>
            </div>

            {/* 테이블 또는 빈 상태 */}
            {filtered.length === 0 ? (
                <EmptyState
                    title="지갑 상태 데이터 없음"
                    description="조건에 맞는 데이터가 없습니다."
                />
            ) : (
                <>
                    {/* 모바일 카드 레이아웃 */}
                    <div className="sm:hidden space-y-2 max-h-96 overflow-y-auto pr-0.5">
                        {filtered.map((item) => {
                            const isHighlighted = (highlightIds || []).includes(`${item.currency}-${item.net_type}`);
                            return (
                                <div
                                    key={item.id || `${item.currency}-${item.net_type}`}
                                    className={`rounded-lg border border-surface-border p-3 transition-colors duration-1000 ${isHighlighted ? "bg-warning-subtle" : "bg-surface-elevated"}`}
                                >
                                    <div className="flex items-center justify-between">
                                        <div className="flex items-center gap-2">
                                            <span className="font-medium text-content">{item.currency}</span>
                                            {renderMarketBadges(item.markets)}
                                        </div>
                                        <span className="text-xs text-content-secondary">{item.net_type || "-"}</span>
                                    </div>
                                    <div className="mt-2 flex gap-3">
                                        <div className="flex items-center gap-1.5">
                                            <span className="text-xs text-content-muted">입금</span>
                                            {renderStatusBadge(item.deposit_status)}
                                        </div>
                                        <div className="flex items-center gap-1.5">
                                            <span className="text-xs text-content-muted">출금</span>
                                            {renderStatusBadge(item.withdrawal_status)}
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>

                    {/* 데스크톱 테이블 레이아웃 */}
                    <div className="hidden sm:block overflow-x-auto max-h-[30rem] overflow-y-auto rounded-lg border border-surface-border">
                        <table className="w-full text-sm">
                            <thead className="sticky top-0 z-10 bg-surface-card">
                                <tr className="text-left text-xs font-semibold text-content-muted uppercase tracking-wider border-b border-surface-border">
                                    <th className="px-3 py-2">코인</th>
                                    <th className="px-3 py-2">마켓</th>
                                    <th className="px-3 py-2">네트워크</th>
                                    <th className="px-3 py-2">입금 상태</th>
                                    <th className="px-3 py-2">출금 상태</th>
                                    <th className="px-3 py-2">최종 업데이트</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filtered.map((item) => {
                                const isHighlighted = (highlightIds || []).includes(`${item.currency}-${item.net_type}`);
                                return (
                                    <tr
                                        key={item.id || `${item.currency}-${item.net_type}`}
                                        className={`border-t border-surface-border transition-colors duration-1000 ${isHighlighted ? "bg-warning-subtle" : "hover:bg-surface-hover/50"}`}
                                    >
                                        <td className="px-3 py-2.5 font-medium text-content">
                                            {item.currency}
                                        </td>
                                        <td className="px-3 py-2.5">
                                            {renderMarketBadges(item.markets)}
                                        </td>
                                        <td className="px-3 py-2.5 text-content-secondary">
                                            {item.net_type || "-"}
                                        </td>
                                        <td className="px-3 py-2.5">
                                            {renderStatusBadge(item.deposit_status)}
                                        </td>
                                        <td className="px-3 py-2.5">
                                            {renderStatusBadge(item.withdrawal_status)}
                                        </td>
                                        <td className="px-3 py-2.5 text-content-secondary text-xs">
                                            {item.updated_at
                                                ? formatKst(item.updated_at)
                                                : "-"}
                                        </td>
                                    </tr>
                                );
                                })}
                            </tbody>
                        </table>
                    </div>
                </>
            )}

            {/* 마지막 업데이트 시각 */}
            {lastUpdated && (
                <p className="mt-2 text-xs text-content-muted">
                    마지막 업데이트: {lastUpdated.toLocaleTimeString("ko-KR")}
                </p>
            )}
        </div>
    );
}

export default WalletStatusTable;
